import Image from "next/image";
import { CheckOut, DollarCircle } from "@/components/icons/assets";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Rating from "@/components/product";


export default function OurStorySection() {
  return (
    <section id="how-it-works-content" className="py-10 sm:py-14 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-20 bg-white">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12 md:mb-16">
        <Badge variant="outline" className="font-plus-jakarta text-xs sm:text-sm font-medium text-neutral-500 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
          How it work
        </Badge>
        <h2 className="font-plus-jakarta text-lg sm:text-2xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
          A SIMPLE, SEAMLESS WAY TO<br className="hidden sm:block" /> FIND BUY & SELL WHEELS
        </h2>
        <p className="font-plus-jakarta text-xs sm:text-sm md:text-lg text-neutral-300 max-w-2xl mx-auto">
          At WheelTox, we’ve streamlined every step — from discovering the perfect fit for your car to selling your used wheels with confidence.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 mb-8 sm:mb-12 md:mb-16">
        <Card className="bg-background-50 border-none rounded-xl shadow-none w-full h-auto lg:h-full lg:min-h-[850px] flex flex-col">
          <CardContent className="p-4 sm:p-6 md:p-8 flex-1 flex flex-col">
            <div>
              <h3 className="font-plus-jakarta text-lg sm:text-xl md:text-2xl font-bold text-neutral-900 mb-2 sm:mb-4">
                SEARCH BY LICENSE PLATE
              </h3>
              <p className="font-plus-jakarta  text-sm text-neutral-300 lg:mb-56 mb-6">
                Enter your license plate number to instantly find all compatible wheels for your car no manual filtering, no guesswork.
              </p>
            </div>

            <Card className="border-none shadow-none rounded-xl bg-white w-full">
              <CardContent className="p-4 sm:p-6">
                {/* Nested Grey Card for Form */}
                <Card className="border-none shadow-none rounded-xl bg-background-50">
                  <CardContent className="p-4 sm:p-6">
                    {/* Form Header */}
                    <div className="mb-6">
                      <h4 className="font-plus-jakarta text-xs font-bold text-neutral-900 mb-1">
                        FIND THE RIGHT WHEELS FOR YOUR CAR.
                      </h4>
                      <p className="font-plus-jakarta text-[11px] text-neutral-300">
                        Discover quality refurbished wheels that fit perfectly a smarter, more sustainable choice.
                      </p>
                    </div>

                    {/* Search Label */}
                    <div className="mb-3">
                      <Label htmlFor="license-plate" className="flex items-center gap-1 font-plus-jakarta text-[11px] font-semibold text-neutral-900 mb-3">
                        <DollarCircle className="w-4 h-4" />
                        Search by License Plate
                      </Label>

                      {/* License Plate Input */}
                      <div className="flex bg-white rounded-lg overflow-hidden h-12 shadow-none">
                        <div className="bg-neutral-50 w-14 flex items-center justify-center font-plus-jakarta text-sm font-bold text-neutral-900">
                          DK
                        </div>
                        <div className="flex-1 px-4 flex items-center font-plus-jakarta text-base font-semibold text-neutral-900 tracking-[0.2em] uppercase">
                          AB12345
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Product Results Card */}
                <Card className="border-none shadow-none rounded-xl bg-white mt-6">
                  <CardContent className="p-0 space-y-6">
                    {/* Product 1 */}
                    <div className="flex gap-4 sm:gap-6 items-center">
                      <div className="w-16 h-16 sm:w-22 sm:h-22 bg-background-50 rounded-xl shrink-0">
                        <Image
                          src="/image/wheels/AlloyWheel1.png"
                          alt="Alloy Wheel"
                          width={112}
                          height={112}
                          className="object-contain w-full h-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-0.5 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className="w-3 h-3 sm:w-4 sm:h-4 text-warning fill-warning"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <h5 className="font-plus-jakarta text-xs sm:text-sm font-semibold text-neutral-900 leading-snug">
                          Alloy Wheel 17×7.5 ET35 - Mercedes / Audi Compatible
                        </h5>
                      </div>
                    </div>

                    {/* Product 2 */}
                    <div className="flex gap-4 sm:gap-6 items-center">
                      <div className="w-16 h-16 sm:w-22 sm:h-22 bg-background-50 rounded-xl shrink-0">
                        <Image
                          src="/image/wheels/SteelWheel1.png"
                          alt="Alloy Wheel"
                          width={112}
                          height={112}
                          className="object-contain w-full h-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex gap-0.5 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <svg
                              key={i}
                              className="w-3 h-3 sm:w-4 sm:h-4 text-warning fill-warning"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <h5 className="font-plus-jakarta text-xs sm:text-sm font-semibold text-neutral-900 leading-snug">
                          Steel Wheel 15x6 – Universal Fit
                        </h5>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Right Column */}
        <div className="flex flex-col gap-3.5 h-auto lg:h-full lg:min-h-[850px]">
          <Card className="bg-background-50 border-none rounded-xl shadow-none py-8 md:py-12 px-6 md:px-8 w-full flex-1 flex flex-col justify-center min-h-[400px] sm:min-h-[auto]">
            <CardContent className="p-0">
              <div className="mb-16 md:mb-28 flex justify-center">
                <CheckOut className="" />
              </div>
              <h3 className="font-plus-jakarta text-xl md:text-2xl font-bold text-neutral-900 mb-4 uppercase">
                CHOOSE & CHECKOUT
              </h3>
              <p className="font-plus-jakarta text-base text-neutral-300">
                Explore top-quality refurbished and used wheels, view detailed specs, and complete a secure checkout in just a few clicks.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-background-50 border-none rounded-xl shadow-none p-6 md:p-8 flex flex-col w-full flex-1">
            <CardContent className="p-0 flex flex-col h-full relative">
              {/* Form Mockup - Centered */}
              <div className="w-full flex justify-center mt-4 mb-auto">
                <Card className="w-[100%] border-none shadow-none relative overflow-hidden bg-white rounded-xl">
                  <CardContent className="p-6">
                    <div
                      className="absolute inset-x-0 bottom-0 h-20 z-10"
                      style={{ background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 29.86%, #F4F4F4 65.66%)' }}
                    ></div>

                    <h4 className="font-plus-jakarta text-base font-bold text-neutral-900 mb-6">
                      SELL YOUR WHEELS EASILY
                    </h4>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-[10px] text-neutral-200 mb-2 font-medium">Full Name</div>
                        <div className="h-8 bg-stroke rounded-full w-full flex items-center px-3">
                          <span className="text-neutral-200 text-xs">__</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-neutral-200 mb-2 font-medium">Email Address</div>
                        <div className="h-8 bg-stroke rounded-full w-full flex items-center px-3">
                          <span className="text-neutral-200 text-xs">__</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      <div>
                        <div className="text-[10px] text-neutral-200 mb-2 font-medium">Phone Number</div>
                        <div className="h-8 bg-stroke rounded-full w-full flex items-center px-3">
                          <span className="text-neutral-200 text-xs">__</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-neutral-200 mb-2 font-medium whitespace-nowrap">License Plate (optional)</div>
                        <div className="h-8 bg-stroke rounded-full w-full flex items-center px-3">
                          <span className="text-neutral-200 text-xs">__</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-neutral-200 mb-2 font-medium">Wheel Condition</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Text Content - Bottom Left */}
              <div className="mt-8">
                <h3 className="font-plus-jakarta text-xl md:text-2xl font-bold text-neutral-900 mb-4">
                  SELL YOUR WHEELS
                </h3>
                <p className="font-plus-jakarta text-base text-neutral-300">
                  Upload your wheel photos, share quick details, and get an instant offer — simple, transparent, and stress-free.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
